import React, { useState, useEffect, useRef } from 'react';
import { Camera, Upload, Loader2, RotateCcw, IdCard, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const SIDES = [{ key: 'frente', label: 'Frente' }, { key: 'verso', label: 'Verso' }];

const CardSlot = ({ token, side, label, initial, onDone }) => {
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const camRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!initial) return;
    fetch(`${API_URL}/public/sign/${token}/cc/${side}`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b) { setPreview(URL.createObjectURL(b)); onDone(side, true); } });
    // eslint-disable-next-line
  }, [initial]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/public/sign/${token}/cc/${side}`, { method: 'POST', body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Falha ao carregar');
      setPreview(data.preview);
      onDone(side, true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pick = (e) => { upload(e.target.files?.[0]); e.target.value = ''; };

  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`cc-slot-${side}`}>
      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
        {preview && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />} {label}
      </p>
      {busy ? (
        <div className="h-28 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : preview ? (
        <img src={preview} alt={`Cartão ${label}`} className="w-full rounded-lg border border-slate-200" data-testid={`cc-preview-${side}`} />
      ) : (
        <div className="h-28 rounded-lg bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
          <IdCard className="w-8 h-8 text-slate-300" />
        </div>
      )}
      <input ref={camRef} type="file" data-max-mb="15" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`cc-camera-input-${side}`} />
      <input ref={fileRef} type="file" data-max-mb="15" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid={`cc-file-input-${side}`} />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" disabled={busy} onClick={() => camRef.current?.click()} className="text-xs bg-[#2D3E6F] text-white rounded-lg py-2 flex items-center justify-center gap-1 disabled:opacity-40" data-testid={`cc-camera-btn-${side}`}>
          {preview ? <RotateCcw className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />} {preview ? 'Repetir' : 'Tirar foto'}
        </button>
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="text-xs border border-slate-300 text-slate-700 rounded-lg py-2 flex items-center justify-center gap-1 disabled:opacity-40" data-testid={`cc-file-btn-${side}`}>
          <Upload className="w-3.5 h-3.5" /> Escolher ficheiro
        </button>
      </div>
      {err && <p className="text-[11px] text-rose-600" data-testid={`cc-error-${side}`}>{err}</p>}
    </div>
  );
};

export const CitizenCardUpload = ({ token, empresa, initial, onChange }) => {
  const [done, setDone] = useState({});
  const mark = (side, ok) => setDone((p) => ({ ...p, [side]: ok }));
  useEffect(() => { onChange(SIDES.every((s) => done[s.key])); /* eslint-disable-next-line */ }, [done]);

  return (
    <div className="space-y-2" data-testid="cc-upload-section">
      <label className="text-xs font-medium text-slate-600 block">Cartão de Cidadão (obrigatório)</label>
      <p className="text-[11px] text-slate-500">
        Tire uma foto nítida da frente e do verso. Será aplicada automaticamente a marca de água
        "CÓPIA EXCLUSIVA PARA A {(empresa || '').replace('_', ' ').toUpperCase()}".
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SIDES.map((s) => (
          <CardSlot key={s.key} token={token} side={s.key} label={s.label} initial={!!initial?.[s.key]} onDone={mark} />
        ))}
      </div>
    </div>
  );
};
